import { useState, useRef, useCallback, useEffect } from "react";
import { CardDropdown } from "./CardDropdown";
import { CardTooltip } from "./CardTooltip";
import { cn } from "@/lib/utils";

interface CardMentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onCardsChange: (cards: Scryfall.Card[]) => void;
  selectedCards: Scryfall.Card[];
  disabled?: boolean;
  placeholder?: string;
  onSubmit: () => void;
}

/**
 * Detect if the cursor is currently inside a [[ ... ]] mention.
 * Returns the query text and the start index of `[[`, or null if not inside.
 */
function detectMentionQuery(
  text: string,
  cursorPos: number
): { query: string; startIdx: number } | null {
  // Look backwards from cursor for `[[`
  const textBeforeCursor = text.slice(0, cursorPos);
  const lastOpen = textBeforeCursor.lastIndexOf("[[");
  if (lastOpen === -1) return null;

  // Make sure there's no `]]` between the `[[` and cursor
  const textBetween = textBeforeCursor.slice(lastOpen + 2);
  if (textBetween.includes("]]")) return null;

  const query = textBetween;
  return { query, startIdx: lastOpen };
}



export function CardMentionInput({
  value,
  onChange,
  onCardsChange,
  selectedCards,
  disabled,
  placeholder,
  onSubmit,
}: CardMentionInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [dropdownState, setDropdownState] = useState<{
    query: string;
    startIdx: number;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-resize textarea to single line height
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [value]);

  const checkForMention = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    const cursorPos = ta.selectionStart;
    const mention = detectMentionQuery(value, cursorPos);

    if (mention && mention.query.length >= 0) {
      setDropdownState({
        query: mention.query,
        startIdx: mention.startIdx,
      });
      setActiveIndex(0);
    } else {
      setDropdownState(null);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    // Check mention on next tick (after value updates)
    setTimeout(() => checkForMention(), 0);
  };

  const handleSelect = (card: Scryfall.Card) => {
    if (!dropdownState) return;

    const before = value.slice(0, dropdownState.startIdx);
    const after = value.slice(
      textareaRef.current?.selectionStart ?? dropdownState.startIdx + 2 + dropdownState.query.length
    );
    const newValue = `${before}[[${card.name}]]${after}`;
    onChange(newValue);

    // Add card to selected cards (avoid duplicates)
    if (!selectedCards.find((c) => c.id === card.id)) {
      onCardsChange([...selectedCards, card]);
    }

    setDropdownState(null);

    // Refocus and move cursor after the inserted mention
    setTimeout(() => {
      const ta = textareaRef.current;
      if (ta) {
        const newCursorPos = before.length + card.name.length + 4; // [[ + name + ]]
        ta.focus();
        ta.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (dropdownState) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => prev + 1);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => Math.max(0, prev - 1));
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setDropdownState(null);
        return;
      }
      // Enter in dropdown mode selects the item (handled by notifying parent)
      // We'll let the dropdown handle this via the active index
    }

    // Submit on Enter (without Shift)
    if (e.key === "Enter" && !e.shiftKey && !dropdownState) {
      e.preventDefault();
      onSubmit();
    }
  };

  // Handle click/cursor movement
  const handleClick = () => {
    setTimeout(() => checkForMention(), 0);
  };

  // Render inline tags display above the textarea showing selected cards
  const renderSelectedTags = () => {
    if (selectedCards.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1 px-1 pb-1">
        {selectedCards.map((card) => (
          <CardTooltip key={card.id} card={card}>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-secondary text-secondary-foreground border border-border">
              {card.name}
              <button
                type="button"
                className="hover:text-destructive ml-0.5"
                onClick={() =>
                  onCardsChange(selectedCards.filter((c) => c.id !== card.id))
                }
              >
                ×
              </button>
            </span>
          </CardTooltip>
        ))}
      </div>
    );
  };

  return (
    <div className="relative w-full flex-1">
      <div
        className={cn(
          "border-input rounded-md border bg-transparent shadow-xs transition-[color,box-shadow] dark:bg-input/30",
          "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]"
        )}
      >
        {renderSelectedTags()}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className="w-full min-w-0 bg-transparent px-3 py-1 text-base outline-none resize-none md:text-sm placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          style={{ maxHeight: "120px" }}
        />
      </div>
      {dropdownState && (
        <CardDropdown
          query={dropdownState.query}
          className="absolute bottom-full left-0 mb-2 z-50 w-full"
          onSelect={handleSelect}
          activeIndex={activeIndex}
        />
      )}
    </div>
  );
}
